import React, { Component } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import MailIcon from "@mui/icons-material/Mail";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import HandshakeIcon from "@mui/icons-material/Handshake";

const getSideBarItems = (accountType) => {
  // Uploader accounts do NOT manage tiers/emails/distributions/store
  const showRestricted = accountType !== 'uploader';
  return [
    {
      id: "Dashboard",
      href: "/dashboard",
      icon: <HomeIcon />,
    },
    ...(showRestricted ? [{
      id: "Tiers",
      href: "/tiers",
      icon: <FormatListNumberedIcon />,
    }] : []),
    {
      id: "Collections",
      href: "/collections",
      icon: <PhotoLibraryIcon />,
    },
    ...(showRestricted ? [{
      id: "Emails",
      href: "/emails",
      icon: <MailIcon />,
    }] : []),
    ...(showRestricted ? [{
      id: "Distributions",
      href: "/distributions",
      icon: <LocalShippingIcon />,
    }] : []),
    ...(showRestricted ? [{
      id: "Store",
      href: "/store",
      icon: <StorefrontIcon />,
    }] : []),
    {
      id: "Activities",
      href: "/activity",
      icon: <ListAltIcon />,
    },
    {
      id: "Settings",
      href: "/settings",
      icon: <SettingsIcon />,
    },
    {
      id: "Linked Accounts",
      href: "/linked-accounts",
      icon: <HandshakeIcon />,
    },
  ];
};

class SideBar extends Component {
  state = {
    storageGb: 0,
    storageGbLimit: 0,
    percent: 0,
    showStorage: true,
    storageDataLoaded: false,
  };

  componentDidMount() {
    this.updateStorageDisplay();
  }

  componentDidUpdate(prevProps) {
    // Update storage display if props changed
    if (prevProps.currentStorage !== this.props.currentStorage || 
        prevProps.maxStorage !== this.props.maxStorage ||
        prevProps.profileLoading !== this.props.profileLoading) {
      this.updateStorageDisplay();
    }
  }

  updateStorageDisplay = () => {
    const { currentStorage, maxStorage, profileLoading } = this.props;
    
    // If profile is still loading, don't update storage display yet
    if (profileLoading) {
      this.setState({ storageDataLoaded: false });
      return;
    }
    
    // Check if props have been initialized (not undefined)
    const propsInitialized = currentStorage !== undefined && maxStorage !== undefined;
    
    // Ensure we have valid numbers
    const currentStorageNum = Number(currentStorage) || 0;
    const maxStorageNum = Number(maxStorage) || 0;
    
    // Convert bytes to GB with proper fallback
    const storageGb = currentStorageNum > 0 ? (currentStorageNum / 1073741824) : 0;
    const storageGbLimit = maxStorageNum > 0 ? (maxStorageNum / 1073741824) : 0;
    
    console.log('SideBar storage update:', { 
      originalProps: { currentStorage, maxStorage, profileLoading },
      propsTypes: { 
        currentStorageType: typeof currentStorage, 
        maxStorageType: typeof maxStorage 
      },
      converted: { currentStorageNum, maxStorageNum },
      inGB: { storageGb: storageGb.toFixed(2), storageGbLimit: storageGbLimit.toFixed(2) },
      propsInitialized,
      willShowStorage: maxStorageNum > 0
    });

    this.setState({
      storageGb: storageGb,
      storageGbLimit: storageGbLimit,
    });

    const percent = maxStorageNum > 0 ? Math.round((currentStorageNum / maxStorageNum) * 100) : 0;

    this.setState({
      percent: Math.min(percent, 100), // Cap at 100%
    });

    // Show storage bar if we have a valid max storage limit
    // Mark as loaded only when props have been explicitly set (not undefined) and profile is not loading
    this.setState({ 
      showStorage: maxStorageNum > 0,
      storageDataLoaded: propsInitialized && !profileLoading
    });
  };

  render() {
    const { percent, storageGb, storageGbLimit, showStorage, storageDataLoaded } = this.state;
    return (
      <Box sx={{ display: { xs: "none", lg: "block" }, mt: 4 }}>
        <List
          component="nav"
          sx={{ position: "fixed", width: "inherit" }}
        >
          {getSideBarItems(this.props.accountType).map((item) => (
            <ListItemButton
              key={item.id}
              component={Link}
              to={item.href}
              selected={this.props.location.pathname === item.href}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.id} />
            </ListItemButton>
          ))}

          {/* Storage display section */}
          {!storageDataLoaded ? (
            // Show loading state while storage data is being fetched
            <ListItem sx={{ pt: 3, justifyContent: "center" }}>
              <CircularProgress size={20} />
            </ListItem>
          ) : showStorage ? (
            <ListItem sx={{ pt: 3, flexDirection: "column", alignItems: "flex-start" }}>
              <Typography variant="caption" color="text.secondary">
                Storage
              </Typography>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{ width: "100%", height: "5px", mb: 0.5 }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography variant="caption" color="text.secondary">
                  {storageGb.toFixed(2)} GB
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {storageGbLimit.toFixed(2)} GB
                </Typography>
              </Box>
            </ListItem>
          ) : (
            <ListItem>
              <Button
                component={Link}
                to="/subscriptions"
                variant="outlined"
                color="primary"
                sx={{ mt: 3, width: "100%" }}
              >
                Subscribe
              </Button>
            </ListItem>
          )}
        </List>
      </Box>
    );
  }
}

const SideBarWithRouter = (props) => {
  const location = useLocation();
  return <SideBar {...props} location={location} />;
};

export default SideBarWithRouter;
